import React, { useMemo } from "react";
import PlatformWrap from "components/PlatformWrap";
import Header from "components/Header";
import PageMHomework from "pages/MHomework";
import useRequest, { errorMessage } from "Utils/useRequest";
import {
    findParentIdByTopicId,
    getHomeworkData,
    IAnswer,
    saveAnswerItem,
    studentDo,
    studentDoSubmit
} from "API";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import _ from "underscore";
import { message, Modal, Spin } from "antd";
import { addBreadcrumb } from "Services/Sentry";
import { Severity } from "@sentry/browser";
import { getExamTypeString, toNumber } from "./utils/filter";
import HomeworkMainPC from "./component/HomeworkMainPC";
import { HomeWorkContext } from "./context";
import LoadingWrapper from "components/LoadingWrapper";

export interface IGroupQuestion {
    topicType: string;
    list: IQuestion[];
}

export interface IQuestion {
    topicId: number;
    topicType: string;
    trunk: string;
    score: string;
    contentText: string | null;
    isRight?: string;
    markScore?: string | null;
    answers: IAnswer[];
    topics?: IQuestion[];
    comment?: string | null | undefined;
    fileList?: string[]; //描述预览列表
    officeUrl?: string; //office前缀
    trunkFileList?: string[]; //题干阅览列表
    trunkOfficeUrl?: string; //office前缀
}

export interface saveAnswer {
    topicId: number;
    topicType: string;
    answers: saveAnswerItem[];
}

export const getSaveAnswer = (answerId: number, content: string) => {
    return { answerId, content, attachment: null };
};

const PageExamMain: React.FC = () => {
    const match = useRouteMatch<{ id: string }>();
    const history = useHistory();

    let [
        courseNo,
        courseSeq,
        weekTime,
        week,
        section,
        continunity,
        homeworkState,
        paperId,
        weekTimeId
    ] = useMemo(
        () => window.atob(match.params.id).split("-"),
        [match.params.id]
    );

    const [data, { loading: examLoading, error }, reloadPaper] = useRequest(
        () =>
            getHomeworkData(homeworkState, {
                courseNo,
                courseSeq,
                weekTime,
                week,
                section,
                continunity,
                paperId
            })
    );

    const onQuestionChange = async (
        topicId: number,
        topicType: string,
        answerId: number | number[],
        value?: string,
        userAnswers?: any
    ) => {
        if (!data) return;
        let removeLayer = addLayer(topicId);

        let answers = [];

        if (_.isArray(answerId)) {
            answerId.toString() !== "" &&
                answerId
                    .toString()
                    .split(",")
                    .map(item =>
                        answers.push({
                            answerId: parseInt(item),
                            content: null,
                            attachment: null
                        })
                    );
        } else {
            answers.push({
                answerId,
                content: value || null,
                attachment: null
            });
        }

        if (topicType === "5") {
            answers = userAnswers.map((item: any) => ({
                answerId: item.answerId,
                content: item.stuAnswer
            }));
        }

        addBreadcrumb(
            "homework",
            parseInt(topicType) !== 4
                ? `学生点击了 ${getExamTypeString(
                      topicType
                  )} 题号为：${topicId} 的 ${answerId} 答案！`
                : `学生做了试题id为 ${topicId} 的主观题！`,
            Severity.Info
        );

        const { HomeworkInfo, examInfo } = data;
        const parentId = findParentIdByTopicId(topicId, data.examList);

        try {
            await studentDo(
                examInfo.courseNo,
                examInfo.courseSeq,
                examInfo.weekTime,
                examInfo.week,
                examInfo.section,
                HomeworkInfo.id,
                topicType,
                topicId,
                parentId,
                weekTimeId,
                answers
            );
            await reloadPaper();
        } catch (err) {
            if (err.isAxiosError && err.message === "Network Error") {
                const msg = "您的网络状态不佳，页面将刷新。";
                message.error(msg);
            } else {
                message.error(errorMessage(err));
            }

            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } finally {
            removeLayer?.();
        }
    };

    const addLayer = (id: number) => {
        const targetDom = document.getElementById(`${id}`);
        if (!targetDom) return;
        targetDom.setAttribute("style", "position: relative");
        const tmpDiv = document.createElement("div");
        tmpDiv.setAttribute(
            "style",
            "position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: 9"
        );
        tmpDiv.onclick = () =>
            message.error(
                "您的作答内容正在保存中，请稍后继续作答，如果持续出现此类错误请刷新页面"
            );
        targetDom.appendChild(tmpDiv);
        function removeLayer() {
            targetDom?.removeAttribute("style");
            targetDom?.removeChild(tmpDiv);
        }

        return removeLayer;
    };

    const onPaperSubmit = () =>
        Modal.confirm({
            title: "提示",
            content: "确认是否提交",
            okText: "确定",
            cancelText: "取消",
            onOk: () => {
                if (!data) return;
                const { HomeworkInfo } = data;

                addBreadcrumb(
                    "homework",
                    `学生执行了点击了交卷按钮！`,
                    Severity.Info
                );

                studentDoSubmit({
                    paperId: HomeworkInfo.id,
                    weekTimeId: weekTimeId
                })
                    .then(res =>
                        message.loading("提交成功！正在跳转首页！", 2).then(
                            res => history.push("/"),
                            err => message.error(err)
                        )
                    )
                    .catch(err => message.error(err.response.data.message));
            }
        });

    const courseInfo = {
        courseName: data?.examInfo.courseName || "",
        courseNo,
        continunity,
        courseSeq,
        week,
        section,
        weekTime,
        date: data?.examInfo.date || ""
    };

    return (
        <HomeWorkContext.Provider value={{ reloadPaper }}>
            <div>
                <PlatformWrap pc={<Header />} />
                <Spin spinning={examLoading}>
                    <LoadingWrapper
                        emptyRender={<Redirect to="/" />}
                        isEmpty={!data}
                        loading={false}>
                        {() => (
                            <PlatformWrap
                                mobile={
                                    <PageMHomework
                                        type="homework"
                                        data={data?.examList || []}
                                        state={toNumber(homeworkState)}
                                        onSubmit={onPaperSubmit}
                                        onQuestionChange={onQuestionChange}
                                    />
                                }
                                pc={
                                    <HomeworkMainPC
                                        courseInfo={courseInfo}
                                        homeworkInfo={data!.HomeworkInfo}
                                        homeworkState={homeworkState}
                                        examList={data?.examList || []}
                                        submitPaper={onPaperSubmit}
                                        saveTopic={onQuestionChange}
                                    />
                                }
                            />
                        )}
                    </LoadingWrapper>
                </Spin>
            </div>
        </HomeWorkContext.Provider>
    );
};

export default PageExamMain;
